import { useCallback, useState } from "react";
import { OrganizationTaskTemplate } from "@common/types/organizationTaskTemplate";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import {
    useArchiveOrganizationTaskTemplateMutation,
    useDeleteOrganizationTaskTemplateMutation,
    useDetachOrganizationTaskTemplateMutation,
    useUnarchiveOrganizationTaskTemplateMutation,
} from "@api/organizationTaskTemplate.api";

interface Params {
    templates: OrganizationTaskTemplate[];
}

export function useOrganizationTaskTemplateLifecycle({ templates }: Params) {
    const { toast: successToast, error: errorToast } = useToaster();
    const [deletingTemplate, setDeletingTemplate] =
        useState<OrganizationTaskTemplate | null>(null);
    const [detachingTemplate, setDetachingTemplate] =
        useState<OrganizationTaskTemplate | null>(null);

    const archiveMutation = useArchiveOrganizationTaskTemplateMutation();
    const unarchiveMutation = useUnarchiveOrganizationTaskTemplateMutation();
    const deleteMutation = useDeleteOrganizationTaskTemplateMutation();
    const detachMutation = useDetachOrganizationTaskTemplateMutation();

    const findTemplate = useCallback(
        (templateId: string) => templates.find((t) => t.id === templateId),
        [templates],
    );

    const handleArchiveTemplate = useCallback(
        async (templateId: string) => {
            const template = findTemplate(templateId);
            if (!template) {
                return;
            }
            try {
                await archiveMutation.mutateAsync(templateId);
                successToast(`Archived task template "${template.title}"`);
            } catch {
                errorToast(
                    `Failed to archive task template "${template.title}"`,
                );
            }
        },
        [archiveMutation, errorToast, findTemplate, successToast],
    );

    const handleUnarchiveTemplate = useCallback(
        async (templateId: string) => {
            const template = findTemplate(templateId);
            if (!template) {
                return;
            }
            try {
                await unarchiveMutation.mutateAsync(templateId);
                successToast(`Enabled task template "${template.title}"`);
            } catch {
                errorToast(
                    `Failed to enable task template "${template.title}"`,
                );
            }
        },
        [unarchiveMutation, errorToast, findTemplate, successToast],
    );

    const handleDetachTemplate = useCallback(
        (templateId: string) => {
            setDetachingTemplate(findTemplate(templateId) ?? null);
        },
        [findTemplate],
    );

    const handleDeleteTemplate = useCallback(
        (templateId: string) => {
            setDeletingTemplate(findTemplate(templateId) ?? null);
        },
        [findTemplate],
    );

    const lifecycleModals = (
        <>
            <ConfirmationModal
                show={!!detachingTemplate}
                title={`Detach "${detachingTemplate?.title}"?`}
                question="Service templates using this task template will no longer be flagged when they fall out of sync with it."
                yes="Detach"
                no="Cancel"
                onReject={() => setDetachingTemplate(null)}
                onHide={() => setDetachingTemplate(null)}
                onConfirm={async () => {
                    if (!detachingTemplate) {
                        return;
                    }
                    const { id, title } = detachingTemplate;
                    try {
                        await detachMutation.mutateAsync(id);
                        setDetachingTemplate(null);
                        successToast(`Detached task template "${title}"`);
                    } catch {
                        errorToast(`Failed to detach task template "${title}"`);
                    }
                }}
            />
            <ConfirmationModal
                show={!!deletingTemplate}
                title={`Delete "${deletingTemplate?.title}"?`}
                question="This task template will be permanently deleted."
                yes="Delete"
                yesBtnVariant="destructive"
                no="Cancel"
                onReject={() => setDeletingTemplate(null)}
                onHide={() => setDeletingTemplate(null)}
                onConfirm={async () => {
                    if (!deletingTemplate) {
                        return;
                    }
                    const { id, title } = deletingTemplate;
                    try {
                        await deleteMutation.mutateAsync(id);
                        setDeletingTemplate(null);
                        successToast(`Deleted task template "${title}"`);
                    } catch {
                        errorToast(`Failed to delete task template "${title}"`);
                    }
                }}
            />
        </>
    );

    return {
        handleArchiveTemplate,
        handleUnarchiveTemplate,
        handleDetachTemplate,
        handleDeleteTemplate,
        lifecycleModals,
    };
}
